import React, { useEffect, useState } from "react";
import "./eligibility.css";
import "./eligibilitymobile.css";
import { calculateEMI } from "./EMICalculator";

const staticLenders = [
  {
    bankName: "HDFC Bank",
    specs: {
      maxLoan: "Upto ₹25L",
      interestRate: "9.99% – 12.5%",
      tenure: "1 – 5 Years",
      processingFee: "₹6,500",
      minAge: "21 Years",
      maxFoir: "70",
    },
    features: ["10 Second Disbursal*", "100% Digital Process*", "Quick Disbursal"],
  },
  {
    bankName: "Axis Bank",
    specs: {
      maxLoan: "Upto ₹40L",
      interestRate: "9.99% – 17.15%",
      tenure: "1 – 5 Years",
      processingFee: "1%-1.5%",
      minAge: "21 Years",
      maxFoir: "70",
    },
    features: ["Low Processing Fee*"],
  },
  {
    bankName: "Kotak Mahindra Bank",
    specs: {
      maxLoan: "Upto ₹25L",
      interestRate: "9.98% – 17.20%",
      tenure: "1 – 5 Years",
      processingFee: "1.1%-1.5%",
      minAge: "23 Years",
      maxFoir: "75",
    },
    features: ["Low Processing Fee*", "100% Digital Process"],
  },
  {
    bankName: "ICICI Bank",
    specs: {
      maxLoan: "Upto ₹25L",
      interestRate: "11% – 13%",
      tenure: "1 – 5 Years",
      processingFee: "Up to 2%",
      minAge: "22 Years",
      maxFoir: "75",
    },
    features: ["100% Digital Process*", "Quick Disbursal"],
  },
];

export default function LenderGrid({
  banks = staticLenders,
  mode = "static",
  sortBy = null,
  loanTenure = 60,
}) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const isDynamic = mode === "dynamic";

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const sortedBanks = [...banks].sort((a, b) => {
    const amtA = parseFloat(a.estimatedLoanAmount) || 0;
    const amtB = parseFloat(b.estimatedLoanAmount) || 0;
    const rateA = parseFloat(a.estimatedRate || a.specs?.interestRate?.split("–")[0]) || Infinity;
    const rateB = parseFloat(b.estimatedRate || b.specs?.interestRate?.split("–")[0]) || Infinity;

    if (sortBy === "amount") return amtB - amtA;
    if (sortBy === "amount-asc") return amtA - amtB;
    if (sortBy === "rate") return rateB - rateA;
    if (sortBy === "rate-asc") return rateA - rateB;
    return 0;
  });

  if (!sortedBanks || sortedBanks.length === 0) {
    return <p>No eligible lenders found.</p>;
  }

  return (
    <div className="lender-comparison-wrapper">
      {sortedBanks.map((bank, idx) => {
        const staticSpec = staticLenders.find(l => l.bankName === bank.bankName)?.specs;

        const values = {
          maxLoan: isDynamic && bank.estimatedLoanAmount
            ? `₹${(bank.estimatedLoanAmount / 100000).toFixed(1)}L`
            : bank.specs?.maxLoan || "-",
          interestRate: isDynamic && bank.estimatedRate
            ? `${parseFloat(bank.estimatedRate).toFixed(2)}%`
            : bank.specs?.interestRate || "-",
          tenure: isDynamic && bank.tenure
            ? `${bank.tenure}`
            : bank.specs?.tenure || "-",
          processingFee: staticSpec?.processingFee || bank.specs?.processingFee || "-",
          emiOrMinAge: isDynamic
            ? (() => {
                const principal = bank.estimatedLoanAmount;
                const rate = parseFloat(bank.estimatedRate);
                const emi = calculateEMI(principal, rate, loanTenure);
                return emi ? `₹${emi}` : "-";
              })()
            : bank.specs?.minAge || "-",
          chanceOrFoir: isDynamic
            ? bank.chance
              ? `${bank.chance}%`
              : "-"
            : bank.specs?.maxFoir || "-",
        };

        return (
          <div key={idx} className="lender-comparison-card">
            <div className="lender-top-row">
              <h4 className="lender-title">{bank.bankName}</h4>
              <div className="lender-feature-list">
                {(bank.features || []).map((feature, i) => (
                  <span key={i} className="feature-pill">{feature}</span>
                ))}
              </div>
            </div>

            {!isMobile ? (
              <div className="lender-specs-grid-7">
                <div>Max Loan</div>
                <div>Rate of Interest</div>
                <div>Tenure</div>
                <div>Processing Fee</div>
                <div>{isDynamic ? "EMI" : "Min Age"}</div>
                <div>{isDynamic ? "Chance" : "Max Foir"}</div>
                <div className="apply-cell">
                  <button className="apply-button-grid">Apply Now</button>
                </div>

                <div><strong>{values.maxLoan}</strong></div>
                <div><strong>{values.interestRate}</strong></div>
                <div><strong>{values.tenure}</strong></div>
                <div><strong>{values.processingFee}</strong></div>
                <div><strong>{values.emiOrMinAge}</strong></div>
                <div><strong>{values.chanceOrFoir}</strong></div>
              </div>
            ) : (
              <div className="lender-specs-grid-mobile">
                <div className="spec-row headings">
                  <div>Max Loan</div>
                  <div>Rate of Interest</div>
                  <div>Tenure</div>
                </div>
                <div className="spec-row results">
                  <div><strong>{values.maxLoan}</strong></div>
                  <div><strong>{values.interestRate}</strong></div>
                  <div><strong>{values.tenure}</strong></div>
                </div>

                <div className="spec-row headings">
                  <div>Processing Fee</div>
                  <div>{isDynamic ? "EMI" : "Min Age"}</div>
                  <div>{isDynamic ? "Chance" : "Max Foir"}</div>
                </div>
                <div className="spec-row results">
                  <div><strong>{values.processingFee}</strong></div>
                  <div><strong>{values.emiOrMinAge}</strong></div>
                  <div><strong>{values.chanceOrFoir}</strong></div>
                </div>

                <div className="apply-cell">
                  <button className="apply-button-grid">Apply Now</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}